import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ArrowLeftIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

export type MobileNavBarProps = Omit<React.ComponentProps<'header'>, 'title'> & {
  /** Screen title, centered (Figma Text/22/Semibold). */
  title: React.ReactNode;
  /** Optional muted line under the title (Figma Text/12/Regular). */
  subtitle?: React.ReactNode;
  /** Leading slot — typically a `MobileNavBar.Back`. Reserves its 40px box. */
  leading?: React.ReactNode;
  /** Trailing slot (Figma's 40px "Actions nav bar"). Reserves its box. */
  trailing?: React.ReactNode;
};

/**
 * The mobile screen header: a back affordance at the leading edge, a centered
 * title + subtitle, and an actions slot at the trailing edge.
 *
 * This is the mobile design's chrome, NOT a narrow `TopNavShellHeader` — the
 * two carry different things (no brand lockup, no tab nav here) and are chosen
 * between rather than reflowed into one another.
 *
 * The title is optically centered by giving BOTH edge slots the same fixed
 * 40px box, so a screen with no trailing action still centers its title the
 * same as one with a trailing action — a `justify-between` row would shift it
 * whenever the trailing slot is empty.
 */
function MobileNavBar({
  className,
  title,
  subtitle,
  leading,
  trailing,
  ...props
}: MobileNavBarProps) {
  return (
    <header
      data-slot="mobile-nav-bar"
      className={cn(
        'flex h-16 w-full shrink-0 items-center gap-4 border-b border-border bg-card px-4 py-2.5',
        className,
      )}
      {...props}
    >
      <div className="flex size-10 shrink-0 items-center justify-center">{leading}</div>

      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5">
        <h1 className="w-full truncate text-center text-[1.375rem] leading-none font-semibold tracking-[-0.04em] text-foreground">
          {title}
        </h1>
        {subtitle !== undefined ? (
          // A bounded flex row, deliberately with NO truncation of its own:
          // the shell's own subtitle strings are short static labels ("Меню")
          // that never overflow, and a rich subtitle (branch name + chevron)
          // must decide for itself which part ellipsizes — a wrapper-level
          // `truncate` would clip a trailing icon away along with the text.
          <p className="flex w-full min-w-0 items-center justify-center gap-1 text-xs leading-none font-normal tracking-tight text-muted-foreground">
            {subtitle}
          </p>
        ) : null}
      </div>

      <div className="flex size-10 shrink-0 items-center justify-end">{trailing}</div>
    </header>
  );
}

export type MobileNavBarBackProps = React.ComponentProps<'button'> & {
  asChild?: boolean;
};

/**
 * The nav bar's back affordance. Renders the arrow glyph itself, so callers
 * supply only the destination — `asChild` with a router `<Link>` for a fixed
 * target, or a plain button with `onClick` for `history.back()`.
 */
function MobileNavBarBack({
  className,
  asChild = false,
  children,
  ...props
}: MobileNavBarBackProps) {
  const Comp = asChild ? Slot : 'button';
  /* The Figma frame (node 9913:27164) sets a 24px icon BOX but insets the
   * glyph to ~16px inside it and draws it with a light stroke. Lucide's
   * default 2px stroke at a full 24px reads far heavier than the design
   * next to the 22px title, so the mark is sized and weighted to match
   * rather than filling its box. */
  const glyph = <ArrowLeftIcon className="size-5" strokeWidth={1} />;
  return (
    <Comp
      data-slot="mobile-nav-bar-back"
      className={cn(
        'flex size-7 cursor-pointer items-center justify-center rounded-3xl text-foreground',
        'transition-colors outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 active:scale-95',
        className,
      )}
      {...props}
    >
      {/* Under `asChild`, Radix's `Slot` MERGES onto the child element rather
       * than wrapping it — so the child must carry the glyph itself, or the
       * arrow replaces it outright and an `asChild` `<Link />` renders as a
       * bare `<svg>` with no anchor and no href (the back button then does
       * nothing). Callers pass an EMPTY element (`<Link to="…" />`); the glyph
       * is injected here so no call site has to know that rule. */}
      {asChild && React.isValidElement(children)
        ? React.cloneElement(
            children as React.ReactElement<{ children?: React.ReactNode }>,
            undefined,
            glyph,
          )
        : glyph}
    </Comp>
  );
}

MobileNavBar.Back = MobileNavBarBack;

export { MobileNavBar, MobileNavBarBack };
